import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil, X, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { ContestBoard, PointsRules } from "@/components/ContestBoard";

const EMPTY = { name: "", prize: "", starts_on: "", ends_on: "", active: true };
const inputCls = "mt-1 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]";

const ContestForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form
      data-testid="contest-form"
      onSubmit={(e) => { e.preventDefault(); onSave(f); }}
      className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
    >
      <div className="md:col-span-2">
        <Label className="text-[10px] tracking-widest uppercase">Contest name</Label>
        <Input required value={f.name} onChange={set("name")} placeholder="Homecoming Weekend Challenge" data-testid="contest-name" className={inputCls} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-[10px] tracking-widest uppercase">Prize</Label>
        <Input value={f.prize} onChange={set("prize")} placeholder="$100 cash" data-testid="contest-prize" className={inputCls} />
      </div>
      <div>
        <Label className="text-[10px] tracking-widest uppercase">Starts</Label>
        <Input required type="date" value={f.starts_on} onChange={set("starts_on")} data-testid="contest-start" className={inputCls} />
      </div>
      <div>
        <Label className="text-[10px] tracking-widest uppercase">Ends</Label>
        <Input required type="date" value={f.ends_on} onChange={set("ends_on")} data-testid="contest-end" className={inputCls} />
      </div>
      <label className="flex items-center gap-2 text-xs uppercase tracking-widest pb-2">
        <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="contest-active" />
        Visible to staff
      </label>
      <div className="flex gap-2">
        <button type="submit" data-testid="contest-save" className="bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs uppercase tracking-widest font-bold">Save</button>
        <button type="button" onClick={onCancel} data-testid="contest-cancel" className="border border-[var(--trio-border)] px-3 py-2"><X className="w-4 h-4" /></button>
      </div>
    </form>
  );
};

export default function ContestsManager() {
  const [contests, setContests] = useState(null);
  const [boards, setBoards] = useState({});
  const [editing, setEditing] = useState(null);

  async function load() {
    try {
      const { data } = await adminApi.get("/admin/contests");
      setContests(data);
      const entries = await Promise.all(data.map((c) => adminApi.get(`/admin/contests/${c.id}/standings`).then((r) => [c.id, r.data.standings])));
      setBoards(Object.fromEntries(entries));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  useEffect(() => { load(); }, []);

  async function save(f) {
    try {
      const { id, state, created_at, ...body } = f;
      if (id) await adminApi.patch(`/admin/contests/${id}`, body);
      else await adminApi.post("/admin/contests", body);
      toast.success("Contest saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function announce(c) {
    const again = c.announced_at ? " It was already announced once — send again?" : "";
    if (!window.confirm(`Email the whole crew the current results of "${c.name}"?${again}`)) return;
    try {
      const { data } = await adminApi.post(`/admin/contests/${c.id}/announce`);
      toast.success(`Announced${data.winner ? `: ${data.winner} wins` : ""} · ${data.sent} emails sent`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function remove(c) {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await adminApi.delete(`/admin/contests/${c.id}`);
    toast.success("Contest deleted");
    load();
  }

  return (
    <div data-testid="contests-manager" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tight">Staff Contests</h2>
          <PointsRules />
          <p className="text-xs text-[var(--trio-muted)] mt-1">Winners are emailed to the whole crew automatically at 10am the morning after a contest ends. Use "Announce Now" anytime.</p>
        </div>
        {!editing && (
          <button onClick={() => setEditing(EMPTY)} data-testid="contest-new" className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs uppercase tracking-widest font-bold">
            <Plus className="w-3.5 h-3.5" /> New Contest
          </button>
        )}
      </div>
      {editing && <ContestForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {contests?.length === 0 && <p className="text-sm text-[var(--trio-muted)]">No contests yet. Create one to get the team competing.</p>}
      {contests?.map((c) => (
        <div key={c.id} className="space-y-2">
          <div className="flex justify-end gap-2">
            {!c.active && <span className="text-[10px] uppercase tracking-widest text-[var(--trio-muted)] self-center">Hidden from staff</span>}
            {c.announced_at && (
              <span data-testid={`contest-announced-${c.id}`} className="text-[10px] uppercase tracking-widest text-green-700 self-center">
                Announced {new Date(c.announced_at).toLocaleDateString()}
              </span>
            )}
            <button onClick={() => announce(c)} data-testid={`contest-announce-${c.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest bg-[var(--trio-text)] text-[var(--trio-bg)] px-3 py-1.5"><Megaphone className="w-3 h-3" /> Announce Now</button>
            <button onClick={() => setEditing(c)} data-testid={`contest-edit-${c.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-[var(--trio-border)] px-3 py-1.5"><Pencil className="w-3 h-3" /> Edit</button>
            <button onClick={() => remove(c)} data-testid={`contest-delete-${c.id}`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest border border-[var(--trio-border)] text-red-700 px-3 py-1.5"><Trash2 className="w-3 h-3" /> Delete</button>
          </div>
          <ContestBoard contest={c} standings={boards[c.id] || []} testid={`contest-board-${c.id}`} />
        </div>
      ))}
    </div>
  );
}
